import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, BookUser, Camera, Smartphone, Mail, FileBadge, Moon, CalendarCheck, HeartPulse, BookOpen, Wallet, Sparkles, Users, Check, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { GuideHero, GoldButton, GhostButton, Reveal, FAQ, GuideCTA } from "../components/GuideBits";
import { REQUIREMENTS } from "../data/hajjGuides";
import { useContent } from "../context/ContentContext";
import { usePageMeta } from "../hooks/usePageMeta";

const docIcons = [BookUser, Camera, Smartphone, Mail, FileBadge];
const eligIcons = [Moon, CalendarCheck, HeartPulse, BookOpen, Wallet, Sparkles, Users];

const HajjRequirements = () => {
  const { lang } = useLang();
  usePageMeta({ title: "Hajj Application Requirements — Checklist from Bangladesh", description: "Documents, eligibility, step-by-step process, women & children rules and FAQs for applying for Hajj from Bangladesh." });
  const { CONTACT, IMAGES } = useContent();
  const c = REQUIREMENTS[lang];

  return (
    <div data-testid="requirements-page">
      <GuideHero
        testid="requirements-hero"
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={c.subtitle}
        image={IMAGES.pageBanner}
        primary={<GoldButton to="/hajj/pre-registration" testid="req-hero-cta">{c.cta_primary} <ArrowRight size={17} /></GoldButton>}
        secondary={<GhostButton href={CONTACT.whatsapp} testid="req-hero-whatsapp"><MessageCircle size={17} /> {c.cta_secondary}</GhostButton>}
        aside={
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.35 }} className="grid grid-cols-2 gap-4">
            {c.docs.slice(0, 4).map((d, i) => {
              const Icon = docIcons[i];
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.12 }} className="rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 p-5 text-white">
                  <Icon size={24} className="text-jannah-gold-light" />
                  <div className="mt-3 font-bold">{d.title}</div>
                </motion.div>
              );
            })}
          </motion.div>
        }
      />

      {/* Documents */}
      <section className="section-pad bg-white" data-testid="req-docs">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-2xl">
            <span className="eyebrow text-jannah-green">{c.docs_eyebrow}</span>
            <div className="gold-divider mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-jannah-navy">{c.docs_title}</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {c.docs.map((d, i) => {
              const Icon = docIcons[i];
              return (
                <Reveal key={i} i={i}>
                  <div className="group h-full rounded-3xl border border-jannah-sand bg-white shadow-ambient p-6 hover:bg-jannah-green-dark hover:text-white hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-xl bg-jannah-green text-white flex items-center justify-center group-hover:bg-jannah-gold transition-colors"><Icon size={22} /></div>
                      <span className="text-4xl font-bold text-jannah-sand group-hover:text-white/15">0{i + 1}</span>
                    </div>
                    <h3 className="mt-5 text-lg font-bold">{d.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-jannah-ink/70 group-hover:text-white/75">{d.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="section-pad bg-jannah-cream" data-testid="req-steps">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4 lg:sticky lg:top-36 self-start">
            <span className="eyebrow text-jannah-green">{c.steps_eyebrow}</span>
            <div className="gold-divider mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-jannah-navy">{c.steps_title}</h2>
            <p className="mt-5 text-jannah-ink/70 leading-relaxed">{c.steps_sub}</p>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-xl relative">
              <img src={IMAGES.whyChoose} alt="" className="w-full h-56 object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-jannah-green-dark/70 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white font-bold flex items-center gap-2"><ShieldCheck size={18} className="text-jannah-gold" /> Fly Jannah Tours & Travels</div>
            </div>
          </Reveal>
          <div className="lg:col-span-8 relative">
            <motion.div initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: "easeOut" }} className="absolute left-[27px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-jannah-gold via-jannah-green to-jannah-gold origin-top" />
            <div className="space-y-5">
              {c.steps.map((s, i) => (
                <Reveal key={i} i={i} className="relative pl-20">
                  <div className="absolute left-0 top-3 w-14 h-14 rounded-full bg-white border-2 border-jannah-gold text-jannah-green font-bold text-lg flex items-center justify-center shadow-md">{i + 1}</div>
                  <div className="rounded-2xl bg-white border border-jannah-sand p-6 hover:shadow-xl hover:border-jannah-green/30 transition-all">
                    <h3 className="text-xl font-bold text-jannah-ink">{s.title}</h3>
                    <p className="mt-2 text-jannah-ink/70 leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="section-pad mesh-band relative overflow-hidden" data-testid="req-eligibility">
        <div className="absolute inset-0 pattern-overlay opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="eyebrow text-jannah-gold-light">{c.elig_eyebrow}</span>
            <div className="gold-divider mx-auto mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-white">{c.elig_title}</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.eligibility.map((e, i) => {
              const Icon = eligIcons[i];
              return (
                <Reveal key={i} i={i} className={i === 6 ? "sm:col-span-2 lg:col-span-1" : ""}>
                  <div className="h-full rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 hover:border-jannah-gold/50 transition-colors">
                    <div className="w-11 h-11 rounded-full bg-jannah-gold/15 border border-jannah-gold/30 flex items-center justify-center"><Icon size={20} className="text-jannah-gold-light" /></div>
                    <h3 className="mt-4 text-lg font-bold text-white">{e.title}</h3>
                    <p className="mt-2 text-white/70 text-sm leading-relaxed">{e.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Women & children */}
      <section className="section-pad bg-white" data-testid="req-women-children">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-2xl">
            <span className="eyebrow text-jannah-green">{c.wc_eyebrow}</span>
            <div className="gold-divider mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-jannah-navy">{c.wc_title}</h2>
          </Reveal>
          <div className="mt-14 grid lg:grid-cols-3 gap-6">
            {[{ t: c.women_title, items: c.women }, { t: c.children_title, items: c.children }].map((blk, bi) => (
              <Reveal key={bi} i={bi}>
                <div className="h-full rounded-3xl border border-jannah-sand bg-white shadow-ambient p-8">
                  <h3 className="text-2xl font-bold text-jannah-ink">{blk.t}</h3>
                  <ul className="mt-5 space-y-3">
                    {blk.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-3 text-jannah-ink/80"><span className="mt-0.5 w-6 h-6 rounded-full bg-jannah-green/10 flex items-center justify-center shrink-0"><Check size={14} className="text-jannah-green" /></span>{it}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
            <Reveal i={2}>
              <div className="h-full rounded-2xl bg-jannah-gold text-white p-8 relative overflow-hidden">
                <div className="absolute -right-6 -bottom-6 w-40 h-40 rounded-full bg-white/10" />
                <FileBadge size={30} />
                <h3 className="mt-4 text-2xl font-bold">{c.mahram_title}</h3>
                <p className="mt-4 leading-relaxed text-white/90">{c.mahram}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-20 bg-jannah-cream" data-testid="req-tips">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-10 items-center">
          <Reveal>
            <span className="eyebrow text-jannah-green">{c.tips_eyebrow}</span>
            <div className="gold-divider mt-3" />
            <h2 className="mt-4 text-3xl md:text-4xl font-bold text-jannah-ink">{c.tips_title}</h2>
          </Reveal>
          <div className="lg:col-span-2 flex flex-wrap gap-3">
            {c.tips.map((tp, i) => (
              <motion.span key={i} initial={{ opacity: 0, scale: 0.85 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-jannah-sand text-jannah-ink font-medium hover:border-jannah-gold hover:shadow-md transition-all cursor-default">
                <span className="w-6 h-6 rounded-full btn-gold text-xs font-bold flex items-center justify-center">{i + 1}</span> {tp}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      <FAQ eyebrow={c.faq_eyebrow} title={c.faq_title} items={c.faqs} testid="req-faq" />
      <GuideCTA title={c.cta_title} sub={c.cta_sub} btnLabel={c.cta_btn} btnTo="/hajj/pre-registration" image={IMAGES.heroSlides[1]} testid="req-cta" />
    </div>
  );
};

export default HajjRequirements;
